<!--
 * @Description: VIP会员订阅弹窗
 * 展示年度和月度会员套餐，支持切换和购买
 * 根据用户是否已充值显示不同的界面
-->
<template>
    <ClientOnly>
        <!-- if already VIP, show subscribed modal -->
        <VipSubscribedModal
            v-if="isVipMember"
            v-model="visible"
            :character-data="characterData"
            @renew="handleRenewClick"
        />

        <!-- if not VIP, show recharge modal -->
        <template v-else>
            <!-- PC: Dialog modal -->
            <el-dialog
                v-if="!isMobile"
                v-model="visible"
                @close="handleClose"
                class="vip-modal"
                :show-close="false"
                :close-on-click-modal="true"
                width="90vw"
                top="5vh"
            >
                <!-- close button -->
                <button
                    class="absolute top-[20px] right-[20px] w-[32px] h-[32px] flex items-center justify-center bg-transparent hover:bg-white/10 rounded-full transition-all duration-200 z-50"
                    @click="handleClose"
                >
                    <svg
                        class="w-[20px] h-[20px] text-white"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M15 5L5 15M5 5L15 15"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </button>

                <!-- main content area - left and right layout -->
                <div class="vip-content-wrapper">
                    <!-- left: character image area -->
                    <div
                        class="vip-left-section"
                        :style="{
                            backgroundImage: `url(${characterBackground})`,
                        }"
                    >
                        <!-- bottom character information -->
                        <div class="character-info">
                            <h3 class="character-name">{{ characterName }}</h3>
                            <div class="character-messages">
                                <div
                                    v-for="(message, index) in selectedMessages"
                                    :key="index"
                                    class="message-item"
                                >
                                    <img
                                        :src="characterAvatar"
                                        :alt="characterName"
                                        class="message-avatar"
                                    />
                                    <p class="message-text">
                                        {{ message }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- right: VIP information area -->
                    <div class="vip-right-section">
                        <!-- background decoration image -->
                        <div class="background-decoration"></div>

                        <!-- loading state -->
                        <div v-if="loading" class="loading-container">
                            <div class="loading-spinner"></div>
                        </div>

                        <!-- content area -->
                        <div v-else-if="!error" class="vip-content">
                            <!-- title and icon -->
                            <div class="vip-header">
                                <img
                                    src="https://static.weryai.com/growth/image/weryai_a655b2df4cfa4f1df92f5458a2380738.png"
                                    alt="皇冠"
                                    class="crown-icon"
                                />
                                <h2 class="vip-title">
                                    {{ t('vip.unlockVipBenefits') }}
                                </h2>
                            </div>

                            <!-- benefits list - 2x3 grid -->
                            <div class="benefits-grid">
                                <div
                                    v-for="(benefit, index) in benefits"
                                    :key="index"
                                    class="benefit-item"
                                >
                                    <img
                                        :src="benefit.icon"
                                        :alt="benefit.text"
                                        class="benefit-icon"
                                    />
                                    <span class="benefit-text">{{
                                        benefit.text
                                    }}</span>
                                </div>
                            </div>

                            <!-- subscription options cards -->
                            <div class="subscription-cards">
                                <!-- annual plan -->
                                <div
                                    v-if="annualPlan"
                                    class="subscription-card card-annual"
                                    :class="{ active: activeTab === 'annual' }"
                                    @click="activeTab = 'annual'"
                                >
                                    <!-- top right corner label -->
                                    <div class="card-badge-corner badge-annual">
                                        <img
                                            src="https://static.weryai.com/growth/image/weryai_23627156413d3c22e17da1a3f0732d0e.png"
                                            :alt="t('vip.preferred')"
                                            class="badge-icon"
                                        />
                                        <span>{{ t('vip.preferred') }}</span>
                                    </div>

                                    <!-- card content -->
                                    <div class="card-content-wrapper">
                                        <!-- left: period and discount -->
                                        <div class="card-left">
                                            <div class="card-period">
                                                {{ t('vip.year') }}
                                            </div>
                                            <div class="card-discount">
                                                {{ t('vip.save50Percent') }}
                                            </div>
                                        </div>

                                        <!-- middle: price -->
                                        <div class="card-center">
                                            <span class="price-amount">{{
                                                annualPlan.price_show
                                            }}</span>
                                        </div>

                                        <!-- right: button -->
                                        <div class="card-right">
                                            <button
                                                class="card-button"
                                                @click.stop="
                                                    handlePurchase(annualPlan)
                                                "
                                            >
                                                {{ t('vip.create') }}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- monthly plan -->
                                <div
                                    v-if="monthlyPlan"
                                    class="subscription-card card-monthly"
                                    :class="{ active: activeTab === 'monthly' }"
                                    @click="activeTab = 'monthly'"
                                >
                                    <!-- top right corner label -->
                                    <div
                                        class="card-badge-corner badge-monthly"
                                    >
                                        <img
                                            src="https://static.weryai.com/growth/image/weryai_55df71e4edb64ddebee66667c6b6c0eb.png"
                                            :alt="t('vip.popular')"
                                            class="badge-icon"
                                        />
                                        <span>{{ t('vip.popular') }}</span>
                                    </div>

                                    <!-- card content -->
                                    <div class="card-content-wrapper">
                                        <!-- left: period and discount -->
                                        <div class="card-left">
                                            <div class="card-period">
                                                {{ t('vip.month') }}
                                            </div>
                                            <div class="card-discount">
                                                {{ t('vip.save35Percent') }}
                                            </div>
                                        </div>

                                        <!-- middle: price -->
                                        <div class="card-center">
                                            <span class="price-amount">{{
                                                monthlyPlan.price_show
                                            }}</span>
                                        </div>

                                        <!-- right: button -->
                                        <div class="card-right">
                                            <button
                                                class="card-button"
                                                @click.stop="
                                                    handlePurchase(monthlyPlan)
                                                "
                                            >
                                                {{ t('vip.create') }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- error state -->
                        <div v-else class="error-container">
                            <svg
                                class="error-icon"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
                                    fill="currentColor"
                                />
                            </svg>
                            <p class="error-text">{{ t('vip.loadFailed') }}</p>
                            <button class="error-retry" @click="loadGoodsList">
                                {{ t('vip.clickRetry') }}
                            </button>
                        </div>
                    </div>
                </div>
            </el-dialog>

            <!-- mobile: Drawer drawer -->
            <el-drawer
                v-else
                v-model="visible"
                direction="btt"
                :show-close="false"
                :close-on-click-modal="true"
                :close-on-press-escape="true"
                :size="'auto'"
                class="vip-drawer"
                @close="handleClose"
            >
                <!-- mobile close button -->
                <button class="mobile-close-button" @click="handleClose">
                    <svg
                        class="w-[20px] h-[20px] text-white"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M15 5L5 15M5 5L15 15"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </button>

                <div class="vip-drawer-content">
                    <!-- character image area (contains all content) -->
                    <div
                        class="vip-section-mobile"
                        :style="{
                            backgroundImage: `url(${characterBackground})`,
                        }"
                    >
                        <!-- gradient content box -->
                        <div class="mobile-content-box">
                            <!-- loading state -->
                            <div v-if="loading" class="loading-container">
                                <div class="loading-spinner"></div>
                            </div>

                            <!-- content area -->
                            <div v-else-if="!error" class="mobile-all-content">
                                <!-- character name -->
                                <h3 class="character-name-mobile">
                                    {{ characterName }}
                                </h3>

                                <!-- chat messages -->
                                <div class="character-messages-mobile">
                                    <div
                                        v-for="(
                                            message, index
                                        ) in selectedMessages"
                                        :key="index"
                                        class="message-wrapper-mobile"
                                    >
                                        <img
                                            :src="characterAvatar"
                                            :alt="characterName"
                                            class="message-avatar-mobile"
                                        />
                                        <div class="message-item-mobile">
                                            <p class="message-text-mobile">
                                                {{ message }}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <!-- VIP title and icon -->
                                <div class="vip-header-mobile">
                                    <img
                                        src="https://static.weryai.com/growth/image/weryai_a655b2df4cfa4f1df92f5458a2380738.png"
                                        alt="crown"
                                        class="crown-icon-mobile"
                                    />
                                    <h2 class="vip-title-mobile">
                                        {{ t('vip.unlockVipBenefits') }}
                                    </h2>
                                </div>

                                <!-- benefits list - one row two columns -->
                                <div class="benefits-grid-mobile">
                                    <div
                                        v-for="(benefit, index) in benefits"
                                        :key="index"
                                        class="benefit-item-mobile"
                                    >
                                        <img
                                            :src="benefit.icon"
                                            :alt="benefit.text"
                                            class="benefit-icon-mobile"
                                        />
                                        <span class="benefit-text-mobile">{{
                                            benefit.text
                                        }}</span>
                                    </div>
                                </div>

                                <!-- subscription options cards -->
                                <div class="subscription-cards">
                                    <!-- annual plan -->
                                    <div
                                        v-if="annualPlan"
                                        class="subscription-card card-annual"
                                        :class="{
                                            active: activeTab === 'annual',
                                        }"
                                        @click="activeTab = 'annual'"
                                    >
                                        <!-- top right corner label -->
                                        <div
                                            class="card-badge-corner badge-annual"
                                        >
                                            <img
                                                src="https://static.weryai.com/growth/image/weryai_23627156413d3c22e17da1a3f0732d0e.png"
                                                :alt="t('vip.preferred')"
                                                class="badge-icon"
                                            />
                                            <span>{{
                                                t('vip.preferred')
                                            }}</span>
                                        </div>

                                        <!-- card content -->
                                        <div class="card-content-wrapper">
                                            <!-- left: period and discount -->
                                            <div class="card-left">
                                                <div class="card-period">
                                                    {{ t('vip.year') }}
                                                </div>
                                                <div class="card-discount">
                                                    {{ t('vip.save50Percent') }}
                                                </div>
                                            </div>

                                            <!-- middle: price -->
                                            <div class="card-center">
                                                <span class="price-amount">{{
                                                    annualPlan.price_show
                                                }}</span>
                                            </div>

                                            <!-- right: button -->
                                            <div class="card-right">
                                                <button
                                                    class="card-button"
                                                    @click.stop="
                                                        handlePurchase(
                                                            annualPlan
                                                        )
                                                    "
                                                >
                                                    {{ t('vip.create') }}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- monthly plan -->
                                    <div
                                        v-if="monthlyPlan"
                                        class="subscription-card card-monthly"
                                        :class="{
                                            active: activeTab === 'monthly',
                                        }"
                                        @click="activeTab = 'monthly'"
                                    >
                                        <!-- 右上角标签 -->
                                        <div
                                            class="card-badge-corner badge-monthly"
                                        >
                                            <img
                                                src="https://static.weryai.com/growth/image/weryai_55df71e4edb64ddebee66667c6b6c0eb.png"
                                                alt="popular"
                                                class="badge-icon"
                                            />
                                            <span>{{ t('vip.popular') }}</span>
                                        </div>

                                        <!-- card content -->
                                        <div class="card-content-wrapper">
                                            <!-- left: period and discount -->
                                            <div class="card-left">
                                                <div class="card-period">
                                                    {{ t('vip.month') }}
                                                </div>
                                                <div class="card-discount">
                                                    {{ t('vip.save35Percent') }}
                                                </div>
                                            </div>

                                            <!-- middle: price -->
                                            <div class="card-center">
                                                <span class="price-amount">{{
                                                    monthlyPlan.price_show
                                                }}</span>
                                            </div>

                                            <!-- right: button -->
                                            <div class="card-right">
                                                <button
                                                    class="card-button"
                                                    @click.stop="
                                                        handlePurchase(
                                                            monthlyPlan
                                                        )
                                                    "
                                                >
                                                    {{ t('vip.create') }}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- error state -->
                            <div v-else class="error-container">
                                <svg
                                    class="error-icon"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
                                        fill="currentColor"
                                    />
                                </svg>
                                <p class="error-text">
                                    {{ t('vip.loadFailed') }}
                                </p>
                                <button
                                    class="error-retry"
                                    @click="loadGoodsList"
                                >
                                    {{ t('vip.clickRetry') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </el-drawer>
        </template>
    </ClientOnly>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted, watch } from 'vue'
import { getGoodsListApi } from '~/api/common'
import { emitOrderGoods } from '~/utils/emitter'
import { useUserStore } from '~/stores/user'
import VipSubscribedModal from './VipSubscribedModal.vue'
import { analyticsEvent } from '~/utils/log-event-func'
import { useI18n } from '~/composables/useI18n'

const { t } = useI18n()

// Props
interface Props {
    modelValue: boolean
    characterData?: any // 角色数据，和 MessageItem 结构一致
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: false,
    characterData: undefined,
})

// Emits
const emit = defineEmits<{
    'update:modelValue': [value: boolean]
}>()

// 弹窗显示状态
const visible = computed({
    get: () => props.modelValue,
    set: value => emit('update:modelValue', value),
})

// 用户store
const userStore = useUserStore()

// 判断是否已经是VIP会员
const isVipMember = computed(() => {
    const accountSummary = userStore.accountSummary
    // 如果有 chat_sub_info 字段，说明已经充值
    return accountSummary && accountSummary.chat_sub_info
})

// 角色相关信息（从传入的数据中获取，如果没有则使用默认值）
const characterName = computed(
    () => props.characterData?.name || 'Chris Girlfriend'
)
const characterAvatar = computed(
    () =>
        props.characterData?.avatar ||
        'https://static.weryai.com/growth/image/weryai_1faa5886411d9dac6fbd0388bbb50d12.png'
)
const characterBackground = computed(
    () =>
        props.characterData?.background ||
        'https://static.weryai.com/growth/image/weryai_1faa5886411d9dac6fbd0388bbb50d12.png'
)

// 四组随机文案的多语言key，每组包含两条消息
const messageGroupKeys = [
    ['vipPromo_missYou_title', 'vipPromo_missYou_text'],
    ['vipPromo_night_title', 'vipPromo_night_text'],
    ['vipPromo_heart_title', 'vipPromo_heart_text'],
    ['vipPromo_punish_title', 'vipPromo_punish_text'],
]

// 随机选择一组消息的key
const selectedMessageKeys = ref<string[]>([])

// 随机选择的消息内容（计算属性，自动响应语言切换）
const selectedMessages = computed(() => {
    return selectedMessageKeys.value.map(key => t(`vip.${key}`))
})

// 随机选择消息组
const selectRandomMessages = () => {
    const randomIndex = Math.floor(Math.random() * messageGroupKeys.length)
    const keys = messageGroupKeys[randomIndex]
    if (keys) {
        selectedMessageKeys.value = [...keys]
    }
}

// 检测是否为移动端
const isMobile = ref(false)

const checkMobile = () => {
    if (import.meta.client) {
        isMobile.value = window.innerWidth < 768
    }
}

// 当前选中的Tab
const activeTab = ref('annual') // 默认选中年度

// 数据状态
const loading = ref(false)
const error = ref(false)
const goodsData = ref<any>(null)

// 会员权益列表（使用图片资源）
const benefits = computed(() => [
    {
        icon: 'https://static.weryai.com/growth/image/weryai_b5f2dc04199353a6590d4938bb3cb4d0.png',
        text: t('vip.unlockAllChatModes'),
    },
    {
        icon: 'https://static.weryai.com/growth/image/weryai_44b6744edd0b7329c4a451735d2677ba.png',
        text: t('vip.unlimitedRegeneration'),
    },
    {
        icon: 'https://static.weryai.com/growth/image/weryai_d72fc3c4f3c9e525d076f02e4e4e4efc.png',
        text: t('vip.characterMemoryEnhancement'),
    },
    {
        icon: 'https://static.weryai.com/growth/image/weryai_0b0975691ab6765e72f364db6e286c75.png',
        text: t('vip.exclusiveMemberBadge'),
    },
    {
        icon: 'https://static.weryai.com/growth/image/weryai_c5cf5a23c5e3181dce36a983bb8af1ef.png',
        text: t('vip.characterResponseSpeed'),
    },
    {
        icon: 'https://static.weryai.com/growth/image/weryai_76a0b832972755a8af6f6bad3c075cbb.png',
        text: t('vip.unlimitedChatAllModes'),
    },
])

// 年度套餐
const annualPlan = computed(() => {
    return goodsData.value?.chat_annual_plan_shelf?.goods?.[0] || null
})

// 月度套餐
const monthlyPlan = computed(() => {
    return goodsData.value?.chat_plan_shelf?.goods?.[0] || null
})

// 加载商品列表
const loadGoodsList = async () => {
    try {
        loading.value = true
        error.value = false

        const response = await getGoodsListApi()

        if (response && response.status === 200 && response.data) {
            goodsData.value = response.data
        } else {
            error.value = true
            console.error('加载VIP套餐失败:', response)
        }
    } catch (err) {
        console.error('加载VIP套餐异常:', err)
        error.value = true
    } finally {
        loading.value = false
    }
}

// 处理购买
const handlePurchase = (plan: any) => {
    if (!plan) {
        console.error('未选择套餐')
        return
    }

    console.log('购买套餐:', plan)

    // 埋点：点击充值会员
    try {
        // 判断是年费还是月费
        const rechargeType = plan === annualPlan.value ? 'yearly' : 'monthly'
        analyticsEvent('click_recharge_chat_pro', {
            character_id: props.characterData?.mate_id?.toString() || '',
            recharge_type: rechargeType,
        })
    } catch (error) {
        console.error('埋点上报失败:', error)
    }

    // 调用全局订单事件，触发支付流程
    // VIP 订单的 subscription_type 为 'CHAT_PLAN'，会自动跳过订阅检查
    emitOrderGoods(plan)

    // 关闭弹窗
    visible.value = false
}

// 关闭弹窗
const handleClose = () => {
    visible.value = false
}

// 处理续费点击（从已充值界面跳转到充值界面）
const handleRenewClick = () => {
    // 这里可以处理续费逻辑
    // 暂时什么都不做，保持弹窗打开，让用户看到充值界面
    console.log('用户点击续费')
}

// 监听弹窗打开，触发埋点
watch(visible, (newValue, oldValue) => {
    // 当弹窗从关闭变为打开时，触发埋点
    if (newValue && !oldValue && !isVipMember.value) {
        try {
            analyticsEvent('view_chat_pro', {
                character_id: props.characterData?.mate_id?.toString() || '',
            })
            loadGoodsList()
            checkMobile()
            // 随机选择消息组
            selectRandomMessages()
            // 监听窗口大小变化
            if (import.meta.client) {
                window.addEventListener('resize', checkMobile)
            }
        } catch (error) {
            console.error('埋点上报失败:', error)
        }
    }
})

onUnmounted(() => {
    if (import.meta.client) {
        window.removeEventListener('resize', checkMobile)
    }
})
</script>

<style scoped lang="less">
/* 主内容布局 */
.vip-content-wrapper {
    display: flex;
    min-height: 600px;
    max-width: 100%;
    background: #0d0e12;
    border-radius: 20px;
    overflow: hidden;
}

/* 左侧区域 */
.vip-left-section {
    flex: 0 0 392px;
    position: relative;
    background: linear-gradient(180deg, #1a1b23 0%, #0d0e12 100%);
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    overflow: hidden;
}

/* 角色信息区域 */
.character-info {
    position: relative;
    z-index: 1;
    padding: 20px;
    background: linear-gradient(
        180deg,
        rgba(13, 14, 18, 0) 0%,
        rgba(13, 14, 18, 0.95) 100%
    );
}

.character-name {
    font-family: Roboto, sans-serif;
    font-weight: 900;
    font-size: 20px;
    line-height: 1.4;
    background: linear-gradient(90.42deg, #ffffff 0.36%, #fdeaa9 99.86%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0 0 16px 0;
}

.character-messages {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.message-item {
    display: flex;
    gap: 10px;
    align-items: flex-start;
}

.message-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    flex-shrink: 0;
    object-fit: cover;
}

.message-text {
    flex: 1;
    background: #ffffff33; /* 和移动端保持一致 */
    backdrop-filter: blur(35.82089614868164px); /* 毛玻璃效果 */
    border-radius: 8px;
    padding: 8px 12px;
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: 400;
    font-size: 12px;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
}

/* 右侧区域 */
.vip-right-section {
    flex: 1;
    position: relative;
    background: #16171d;
    padding: 32px 28px;
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
}

/* 背景装饰图 */
.background-decoration {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-image: url('https://static.weryai.com/growth/image/weryai_66f83912d7753f36a816ca9aaab34582.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    pointer-events: none;
}

/* Loading容器 */
.loading-container {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
}

.loading-spinner {
    width: 48px;
    height: 48px;
    border: 2px solid rgba(153, 247, 104, 0.2);
    border-top-color: #99f768;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* 内容区域 */
.vip-content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;
    max-width: 100%;
}

/* 标题区域 */
.vip-header {
    display: flex;
    align-items: center;
    gap: 10px;
}

.crown-icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
}

.vip-title {
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: 600;
    font-size: 18px;
    line-height: 1.4;
    background: linear-gradient(90.42deg, #ffffff 0.36%, #fdeaa9 99.86%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0;
}

/* 权益网格 */
.benefits-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px 20px;
}

.benefit-item {
    display: flex;
    align-items: center;
    gap: 12px;
}

.benefit-icon {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    object-fit: contain;
}

.benefit-text {
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.5;
    color: #ffffff;
}

/* 订阅卡片容器 */
.subscription-cards {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
}

/* 订阅卡片基础样式 */
.subscription-card {
    position: relative;
    width: 480px;
    height: 105px;
    max-width: 100%;
    border-radius: 16px;
    cursor: pointer;
    transition: all 0.3s ease;
    overflow: hidden;
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-position: center;
    transform-origin: left center;

    &:hover {
        transform: translateY(-1px);
    }
}

/* 年度套餐样式 */
.card-annual {
    background-image: url('https://static.weryai.com/growth/image/weryai_9181928a97d1001497dd940eff3eae7a.png');

    &:hover,
    &.active {
        box-shadow: 0 4px 16px rgba(247, 208, 70, 0.2);
    }
}

/* 月度套餐样式 */
.card-monthly {
    background-image: url('https://static.weryai.com/growth/image/weryai_a69ba4f7b111c7129364b171f2c96c88.png');
}

/* 右上角标签 */
.card-badge-corner {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    align-items: center;
    gap: 3px;
    padding: 4px 10px;
    border-radius: 0 0px 0 20px;
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: 500;
    font-size: 11px;
    line-height: 1.3;

    .badge-icon {
        width: 12px;
        height: 12px;
        object-fit: contain;
    }
}

.badge-annual {
    background: linear-gradient(95.35deg, #fedf62 0%, #ffecd0 97.05%);
    color: #000000;
}

.badge-monthly {
    background: linear-gradient(99.21deg, #b05cff 0.24%, #e9b9ff 100.24%);
    color: #5a198b;
}

/* 卡片内容布局 */
.card-content-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    height: 100%;
    padding: 0 20px;
}

/* 左侧：周期和折扣 */
.card-left {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex-shrink: 0;
}

.card-period {
    font-family: Inter, sans-serif;
    font-weight: 600;
    font-size: 20px;
    line-height: 21px;
    color: #ffffff;
}

.card-annual .card-period {
    color: #ffd632; /* 年字颜色 */
}

.card-discount {
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: 400;
    font-size: 12px;
    line-height: 100%;
    white-space: nowrap;
}

.card-annual .card-discount {
    color: #f7d046;
}

.card-monthly .card-discount {
    color: #ffffff;
}

/* 中间：价格 */
.card-center {
    display: flex;
    align-items: center;
    flex: 1;
    justify-content: flex-end;
    min-width: 0;
    padding-right: 10px;
}

.price-amount {
    font-family: Inter, sans-serif;
    font-weight: 700;
    font-size: 24px;
    line-height: 40px;
    color: #ffffff;
}

/* 右侧：按钮 */
.card-right {
    flex-shrink: 0;
}

.card-button {
    padding: 0 24px;
    height: 40px;
    border: none;
    border-radius: 10px;
    font-family: Roboto, sans-serif;
    font-weight: 600;
    font-size: 14px;
    line-height: 1.4;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
}

.card-annual .card-button {
    background: #ffdc82;
    backdrop-filter: blur(10px);
    color: #000000;

    &:hover {
        background: #ffd666;
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(255, 220, 130, 0.4);
    }
}

.card-monthly .card-button {
    background: #ffffff;
    backdrop-filter: blur(10px);
    color: #000000;

    &:hover {
        background: #f0f0f0;
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(255, 255, 255, 0.3);
    }
}

.card-button:active {
    transform: translateY(0);
}

/* 错误容器 */
.error-container {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    gap: 16px;
}

.error-icon {
    width: 64px;
    height: 64px;
    color: rgba(255, 255, 255, 0.2);
}

.error-text {
    font-family: Roboto, sans-serif;
    font-weight: 500;
    font-size: 18px;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.6);
    margin: 0;
}

.error-retry {
    padding: 8px 24px;
    background: transparent;
    border: 1px solid #99f768;
    border-radius: 8px;
    font-family: Roboto, sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 1.4;
    color: #99f768;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(153, 247, 104, 0.1);
    }
}

/* 中等屏幕缩放 */
@media (max-width: 600px) {
    .subscription-card {
        transform: scale(0.7);
        margin-bottom: -32px;

        &:hover {
            transform: scale(0.7) translateY(-1px);
        }
    }
}

/* 小屏幕进一步缩放 */
@media (max-width: 450px) {
    .subscription-card {
        transform: scale(0.6);
        margin-bottom: -42px;

        &:hover {
            transform: scale(0.6) translateY(-1px);
        }
    }
}

/* 响应式调整 */
@media (max-width: 768px) {
    .vip-content-wrapper {
        flex-direction: column;
        min-height: auto;
    }

    .vip-left-section {
        flex: 0 0 300px;
        background-size: cover;
        background-position: center top;
    }

    .character-info {
        padding: 16px;
    }

    .character-name {
        font-size: 16px;
        margin-bottom: 12px;
    }

    .message-avatar {
        width: 32px;
        height: 32px;
    }

    .message-text {
        font-size: 11px;
        padding: 6px 10px;
    }

    .vip-right-section {
        padding: 20px 16px;
    }

    .vip-content {
        gap: 20px;
    }

    .vip-header {
        gap: 8px;
    }

    .crown-icon {
        width: 20px;
        height: 20px;
    }

    .vip-title {
        font-size: 16px;
    }

    .benefits-grid {
        grid-template-columns: 1fr;
        gap: 12px;
    }

    .benefit-icon {
        width: 20px;
        height: 20px;
    }

    .benefit-text {
        font-size: 13px;
    }

    .subscription-cards {
        gap: 0;
        align-items: flex-start;
    }

    .subscription-card {
        width: 480px;
        height: 105px;
        transform: scale(0.75);
        transform-origin: left center;
        margin-bottom: -26px;
        border-radius: 12px;
        background-size: 100% 100%;

        &:hover {
            transform: scale(0.75) translateY(-1px);
        }

        &:last-child {
            margin-bottom: 0;
        }
    }

    .card-content-wrapper {
        padding: 0 20px;
        gap: 12px;
    }

    .card-badge-corner {
        right: 16px;
        padding: 4px 10px;
    }
}
</style>

<style lang="less">
/* 全局样式 - 覆盖 Element Plus Dialog */
.el-dialog.vip-modal {
    background: transparent !important;
    box-shadow: none !important;
    padding: 0 !important;
    border-radius: 20px !important;
    overflow: hidden !important;
    max-width: 880px !important;
    margin-top: 15vh !important;

    .el-dialog__header {
        display: none !important;
        padding: 0 !important;
        margin: 0 !important;
        height: 0 !important;
        min-height: 0 !important;
    }

    .el-dialog__body {
        padding: 0 !important;
        margin: 0 !important;
        background: transparent !important;
    }

    .el-dialog__footer {
        display: none !important;
        padding: 0 !important;
        margin: 0 !important;
        height: 0 !important;
        min-height: 0 !important;
    }
}

@media (max-width: 768px) {
    .el-dialog.vip-modal {
        margin: 20px !important;
        max-width: calc(100vw - 40px) !important;
    }
}

/* 移动端Drawer样式 */
.el-drawer.vip-drawer {
    background: #0d0e12 !important;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3) !important;
    border-radius: 20px 20px 0 0 !important;
    overflow: hidden !important;
    max-height: 90vh !important;

    .el-drawer__header {
        display: none !important;
        padding: 0 !important;
        margin: 0 !important;
        height: 0 !important;
        min-height: 0 !important;
    }

    .el-drawer__body {
        padding: 0 !important;
        margin: 0 !important;
        background: transparent !important;
        overflow-y: auto !important;
    }

    .el-drawer__footer {
        display: none !important;
        padding: 0 !important;
        margin: 0 !important;
        height: 0 !important;
        min-height: 0 !important;
    }
}

/* 移动端关闭按钮 */
.mobile-close-button {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 100;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(10px);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border: none;
    transition: background 0.3s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.7);
    }

    svg {
        width: 20px;
        height: 20px;
        color: #ffffff;
    }
}

/* 移动端抽屉内容 */
.vip-drawer-content {
    display: flex;
    flex-direction: column;
    background: #0d0e12;
    min-height: 85vh;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
}

/* 移动端图片区域 */
.vip-section-mobile {
    flex: 1;
    position: relative;
    background-size: 100% auto; /* 宽度100%，高度自适应 */
    background-position: top center;
    background-repeat: no-repeat;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    overflow: hidden;
    min-height: 85vh;
}

/* 移动端渐变内容盒子 */
.mobile-content-box {
    position: relative;
    z-index: 1;
    background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 0%,
        #000000 26.49%,
        #000000 100%
    );
    margin-top: 159px; /* 盒子距离顶部159px */
    padding: 16px 16px 20px; /* 内部padding */
    display: flex;
    flex-direction: column;
    flex: 1;
}

/* 移动端所有内容容器 */
.mobile-all-content {
    display: flex;
    flex-direction: column;

    /* 移动端订阅卡片样式调整 */
    .subscription-cards {
        display: flex;
        flex-direction: column;
        gap: 10px;
        align-items: stretch; /* 改为stretch让卡片填充整个宽度 */
        width: 100%;
    }

    .subscription-card {
        width: 100%; /* 改为100%自适应宽度 */
        height: auto; /* 高度自适应 */
        aspect-ratio: 4.6; /* 保持宽高比 345:75 ≈ 4.6:1 */
        min-height: 60px; /* 设置最小高度，防止太小 */
        /* 移除max-height限制，让高度可以随宽度按比例增长 */
        transform: none; /* 不需要缩放 */
        transform-origin: left center;
        margin-bottom: 0; /* 不需要负边距 */

        &:hover {
            transform: translateY(-1px);
        }

        &:last-child {
            margin-bottom: 0;
        }
    }

    /* 右上角徽章 */
    .card-badge-corner {
        min-width: 53.5px; /* 改为最小宽度，允许内容撑开 */
        height: auto; /* 高度自适应 */
        padding: 4px 8px; /* 添加上下左右padding */
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 2px;
        right: 0;
        top: 0;
        border-radius: 0 0px 0 12px;

        .badge-icon {
            width: clamp(7px, 2.2vw, 10px); /* 图标响应式 */
            height: clamp(7px, 2.2vw, 10px);
        }

        span {
            font-size: clamp(10px, 3vw, 12px); /* 响应式字体 */
            line-height: 1;
            white-space: nowrap; /* 防止文字换行 */
        }
    }

    /* 卡片内容 */
    .card-content-wrapper {
        padding: 0 4%; /* 改为百分比padding，适应不同屏幕 */
        gap: 2%; /* 改为百分比gap */
        height: 100%;
    }

    /* 左侧：周期和折扣 */
    .card-left {
        gap: 2px;

        .card-period {
            font-size: clamp(14px, 4.2vw, 20px); /* 大标题，响应式字体 */
            line-height: 1.2;
        }

        .card-discount {
            font-size: clamp(10px, 3vw, 12px); /* 节省文案，响应式字体 */
            line-height: 1;
        }
    }

    /* 年卡的"年"字颜色 */
    .card-annual .card-period {
        color: #ffd632;
    }

    /* 中间：价格 */
    .card-center {
        .price-amount {
            font-size: clamp(16px, 5vw, 22px); /* 金额，响应式字体 */
            line-height: 1.2;
            padding-right: 10px;
        }
    }

    /* 右侧：按钮 */
    .card-right {
        .card-button {
            min-width: 55px; /* 最小宽度 */
            width: auto; /* 宽度自适应 */
            height: auto; /* 高度自适应 */
            padding: 8px 16px; /* 使用padding控制按钮大小 */
            font-size: clamp(11px, 3.2vw, 14px); /* 响应式字体 */
            border-radius: 8px;
            white-space: nowrap; /* 防止文字换行 */
        }
    }
}

.character-name-mobile {
    font-family: Roboto, sans-serif;
    font-weight: 900;
    font-size: 21px; /* 21px */
    line-height: 1.4;
    background: linear-gradient(90.42deg, #ffffff 0.36%, #fdeaa9 99.86%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
    margin: 0 0 20px 0;
}

.character-messages-mobile {
    display: flex;
    flex-direction: column;
    gap: 12px; /* 两条消息之间的间距 */
    margin-bottom: 30px;
}

.message-wrapper-mobile {
    display: flex;
    align-items: flex-start;
    gap: 10px;
}

.message-avatar-mobile {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
}

.message-item-mobile {
    flex: 1;
    background: #ffffff33; /* 修改背景颜色 */
    backdrop-filter: blur(35.82089614868164px); /* 毛玻璃效果 */
    border-radius: 8px;
    padding: 8px 12px;
}

.message-text-mobile {
    font-family: 'Source Han Sans CN', sans-serif;
    font-weight: 400;
    font-size: 14px; /* 14px */
    line-height: 1.5;
    color: #ffffff;
    margin: 0;
}

/* 移动端VIP标题区域 */
.vip-header-mobile {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;

    .crown-icon-mobile {
        width: 26px !important; /* 26x26 */
        height: 26px !important;
        object-fit: contain;
    }

    .vip-title-mobile {
        font-family: Roboto, sans-serif;
        font-weight: 700;
        font-size: 21px !important; /* 21px */
        line-height: 1.2;
        background: linear-gradient(90.42deg, #ffffff 0.36%, #fdeaa9 99.86%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        color: transparent;
        margin: 0;
    }
}

/* 移动端权益列表 */
.benefits-grid-mobile {
    display: grid;
    grid-template-columns: repeat(2, 1fr); /* 一行两个 */
    gap: 9px 20px;
    margin-bottom: 20px;

    .benefit-item-mobile {
        display: flex;
        align-items: center;
        gap: 8px;

        .benefit-icon-mobile {
            width: 23px !important; /* 23x23 */
            height: 23px !important;
            object-fit: contain;
            flex-shrink: 0;
        }

        .benefit-text-mobile {
            font-family: 'Source Han Sans CN', sans-serif;
            font-weight: 400;
            font-size: 14px !important; /* 14px */
            line-height: 1.5;
            color: #ffffff;
        }
    }
}
</style>
